/**
 * Repository browser — lists every concept in the workspace, grouped by
 * its element type. Three things you can do:
 *
 * 1. **Click** a concept to select it in the attribute panel for editing.
 * 2. **Drag** a concept onto the canvas to place it on the active diagram
 *    (no new concept is created — the diagram's view_state simply gets a
 *    position entry, so the same concept can appear on multiple diagrams).
 * 3. **Search** by name (NL + EN) to filter the list.
 *
 * Implementation note: the panel subscribes to the workspace revision so
 * the list rebuilds whenever a concept is added/edited/removed elsewhere.
 */
import { fuzzyMatchAny } from "@ae/domain";
import { useDeferredValue, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { allowedTypeIdsForViewKind, getRegistry, resolveBilingual } from "../../registry";
import { useSession } from "../../state/session";
import { useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";
import { confirmHardDelete } from "../cascadeConfirm";
import { colorForLayer } from "../layerColors";
import { PanelCollapseIcon } from "../PanelCollapseIcon";
import { panelSwatch } from "../panelSwatch";
import { useMultiSelect } from "../useMultiSelect";
import { usePanelWidth } from "../usePanelWidth";

export const EXISTING_CONCEPT_MIME = "application/ae-existing-concept-id";
export const EXISTING_CONCEPT_BULK_MIME = "application/ae-existing-concept-ids";
export const EXISTING_DIAGRAM_MIME = "application/ae-existing-diagram-id";

export function ConceptExplorer() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const open = useUi((s) => s.conceptExplorerOpen);
  const setOpen = useUi((s) => s.setConceptExplorerOpen);
  const pinned = useUi((s) => s.conceptExplorerPinned);
  const setPinned = useUi((s) => s.setConceptExplorerPinned);
  const { width: panelWidth, ResizeHandle } = usePanelWidth(
    "ae.panelWidth.conceptExplorer",
    240,
    "right",
  );
  const selectedConceptId = useUi((s) => s.selectedConceptId);
  const selectConcept = useUi((s) => s.selectConcept);
  const revision = useWorkspaceData((s) => s.revision);
  const activeDiagramId = useWorkspaceData((s) => s.activeDiagramId);
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  void revision;

  const [query, setQuery] = useState("");
  // useDeferredValue zorgt dat het uitfilteren van de lijst niet de
  // typing-render blokkeert wanneer er veel concepten zijn (500+).
  // React mag de oude lijst eerst tonen terwijl de nieuwe filter op
  // de achtergrond gerekend wordt.
  const deferredQuery = useDeferredValue(query);
  // `collapsedTypes[id]` slaat ALLEEN expliciete user-toggle op:
  //   - true  = user heeft 'm dichtgeklapt
  //   - false = user heeft 'm expliciet open geklapt
  //   - undefined = volg de default (zie `defaultCollapsed` hieronder)
  const [collapsedTypes, setCollapsedTypes] = useState<Record<string, boolean | undefined>>({});
  const multi = useMultiSelect();
  // When true, the list shows ONLY concepts whose typeId is allowed on
  // the active diagram's view-kind. When false, every workspace concept
  // is visible (useful when you want to see what exists, even if the
  // current diagram-type doesn't accept it).
  const [filterToActiveDiagram, setFilterToActiveDiagram] = useState(true);
  // Multi-select sets — empty set means "no restriction on this axis".
  // Stored as Set<string> in state via array round-trip so React state
  // identity changes correctly trigger memo re-runs.
  const [layerFilter, setLayerFilter] = useState<Set<string>>(new Set());
  const [ownerFilter, setOwnerFilter] = useState<Set<string>>(new Set());
  const [tagFilter, setTagFilter] = useState<Set<string>>(new Set());
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const setActiveDiagram = useWorkspaceData((s) => s.setActiveDiagram);

  // Read the active diagram's view-kind so we can filter and react to
  // diagram-type changes (the registry lookup itself is sync, but we
  // need the React subscription to trigger a re-render).
  const activeViewKind = useWorkspaceData((s) => {
    if (!s.activeDiagramId) return null;
    return s.store.getDiagram(s.activeDiagramId)?.viewKind ?? null;
  });

  const grouped = useMemo(() => {
    const store = useWorkspaceData.getState().store;
    const reg = getRegistry();
    const allowed =
      filterToActiveDiagram && activeViewKind ? allowedTypeIdsForViewKind(activeViewKind) : null; // null = no filter
    const concepts = store
      .listConcepts()
      .filter((c) => !c.tombstoneInBranch)
      // Drawing-primitieven (rechthoek, lijn, beschrijving-box) zijn
      // visuele annotaties zonder semantische betekenis. Tonen ze in
      // de Explorer-tree zou de lijst alleen vervuilen — modelleurs
      // werken er via de canvas mee, niet via de browse-laag.
      .filter((c) => !c.typeId.startsWith("drawing."))
      .filter((c) => allowed === null || allowed.has(c.typeId))
      .filter((c) => {
        if (layerFilter.size === 0) return true;
        const t = reg.getElementType(c.typeId);
        return layerFilter.has(t?.layer ?? "other");
      })
      .filter((c) => ownerFilter.size === 0 || ownerFilter.has(c.ownerEmail))
      .filter((c) => {
        if (tagFilter.size === 0) return true;
        return c.tags.some((t) => tagFilter.has(t));
      })
      .filter((c) => {
        if (!deferredQuery.trim()) return true;
        return fuzzyMatchAny(Object.values(c.name), deferredQuery);
      });

    // Group by typeId. Keep type label + ordering so the panel matches
    // the type-palette layout. Within a group, build a hierarchical tree
    // when same-type Composition relations exist (a Goedkeuring Process
    // composing an Aanvraag Process should render indented under it).
    const conceptsByType = new Map<string, typeof concepts>();
    for (const c of concepts) {
      const arr = conceptsByType.get(c.typeId) ?? [];
      arr.push(c);
      conceptsByType.set(c.typeId, arr);
    }

    const groups: GroupedConcepts[] = [];
    for (const [typeId, items] of conceptsByType) {
      const t = reg.getElementType(typeId);
      const typeLabel = t ? resolveBilingual(t.label, lang) : typeId;
      const layer = t?.layer ?? "other";
      const tree = buildCompositionTree(items, store, lang);
      groups.push({ typeId, typeLabel, layer, tree });
    }

    return groups.sort((a, b) => a.typeLabel.localeCompare(b.typeLabel));
  }, [
    revision,
    deferredQuery,
    lang,
    filterToActiveDiagram,
    activeViewKind,
    layerFilter,
    ownerFilter,
    tagFilter,
  ]);

  // Discover the universe of layers / owners / tags for the filter UI.
  // Computed off the unfiltered concept set so the filter chips don't
  // disappear once the user narrows the list.
  const filterUniverse = useMemo(() => {
    void revision;
    const store = useWorkspaceData.getState().store;
    const reg = getRegistry();
    const layers = new Set<string>();
    const owners = new Set<string>();
    const tags = new Set<string>();
    for (const c of store.listConcepts()) {
      if (c.tombstoneInBranch) continue;
      const t = reg.getElementType(c.typeId);
      layers.add(t?.layer ?? "other");
      owners.add(c.ownerEmail);
      for (const tg of c.tags) tags.add(tg);
    }
    return {
      layers: [...layers].sort(),
      owners: [...owners].sort(),
      tags: [...tags].sort(),
    };
  }, [revision]);

  // Map of conceptId → array of diagramIds that visualise it. Used for
  // the "Gebruikt in N diagrammen" badge + click-through. We compute it
  // once here so the row-renderer doesn't re-walk every diagram per row.
  const usageByConceptId = useMemo(() => {
    void revision;
    const store = useWorkspaceData.getState().store;
    const map = new Map<string, string[]>();
    for (const d of store.listDiagrams()) {
      for (const [key, ve] of Object.entries(d.viewState)) {
        if (ve.position === undefined) continue;
        const conceptId = ve.ref ?? key;
        const arr = map.get(conceptId) ?? [];
        if (!arr.includes(d.id)) arr.push(d.id);
        map.set(conceptId, arr);
      }
    }
    return map;
  }, [revision]);

  // Read which concepts are actually placed on the active diagram (i.e.
  // have a position in the diagram's view_state). The Explorer paints a
  // small green dot for those so you can see what's already on the
  // canvas vs. what's still in the workspace but not on this diagram.
  const onActiveDiagram = useMemo(() => {
    if (!activeDiagramId) return new Set<string>();
    const store = useWorkspaceData.getState().store;
    const diagram = store.getDiagram(activeDiagramId);
    if (!diagram) return new Set<string>();
    return new Set(
      Object.entries(diagram.viewState)
        // `position` (free-form) of `slotId` (canvas-mode bmc.canvas) — beide
        // betekenen "concept zit op dit diagram". Relation-only entries
        // hebben geen van beide en worden geskipt.
        .filter(([, ve]) => ve.position !== undefined || ve.slotId !== undefined)
        .map(([id]) => id),
    );
  }, [revision, activeDiagramId]);

  // Flat list of every concept id currently visible (skipping collapsed
  // type groups). Used for shift-range selection so a Shift+Click picks
  // the contiguous run between the anchor and the click target as the
  // user actually sees them. NOTE: must stay above the early-return for
  // `!open` — Rules of Hooks require a stable call order.
  const visibleOrder = useMemo(() => {
    const order: string[] = [];
    const walk = (node: TreeNode) => {
      order.push(node.concept.id);
      for (const ch of node.children) walk(ch);
    };
    // We can't use `isCollapsed` here directly omdat dit boven de
    // early-return staat (Rules of Hooks); kleine duplicatie van de
    // logica is acceptabel. `defaultCollapsed` hangt af van runtime-
    // count en query, dus inline herberekenen.
    const total = grouped.reduce((s, g) => s + countTreeNodes(g.tree), 0);
    const defaultCollapsedHere = total > 100 && !deferredQuery.trim();
    for (const g of grouped) {
      const explicit = collapsedTypes[g.typeId];
      const collapsed = explicit !== undefined ? explicit : defaultCollapsedHere;
      if (collapsed) continue;
      for (const t of g.tree) walk(t);
    }
    return order;
  }, [grouped, collapsedTypes, deferredQuery]);

  if (!open) return null;

  const totalCount = grouped.reduce((sum, g) => sum + countTreeNodes(g.tree), 0);
  // Default collapse-gedrag: bij grote workspaces (>100 concepten) en
  // geen actieve zoekterm zijn alle groepen dichtgeklapt zodat de
  // initial render snel is. Tijdens zoeken klappen we juist alles
  // open zodat matches niet verborgen blijven achter een collapsed
  // header. Expliciete user-toggle in `collapsedTypes` overruled altijd.
  const defaultCollapsed = totalCount > 100 && !deferredQuery.trim();
  const isCollapsed = (typeId: string): boolean => {
    const explicit = collapsedTypes[typeId];
    if (explicit !== undefined) return explicit;
    return defaultCollapsed;
  };

  return (
    <aside
      className="ledger relative flex h-full shrink-0 flex-col border-(--ld-control) border-r bg-(--ld-surface)"
      style={{ width: panelWidth }}
    >
      <header className="flex items-center justify-between border-(--ld-border) border-b px-3 py-2">
        <div className="flex items-baseline gap-2">
          <h2 className="font-semibold text-(--ld-text) text-sm">{t("app.panels.concepts")}</h2>
          <span className="text-(--ld-muted) text-xs">{totalCount}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className={`rounded px-1 text-sm leading-none ${
              pinned
                ? "text-(--ld-accent) hover:text-(--ld-accent)"
                : "text-(--ld-muted) hover:text-(--ld-text)"
            }`}
            onClick={() => setPinned(!pinned)}
            title={pinned ? t("conceptExplorer.pinnedTitle") : t("conceptExplorer.pinTitle")}
            aria-label={pinned ? t("conceptExplorer.unpinAria") : t("conceptExplorer.pinAria")}
            aria-pressed={pinned}
          >
            {pinned ? "📌" : "📍"}
          </button>
          <button
            type="button"
            className="flex h-7 w-7 items-center justify-center rounded text-(--ld-text) text-base leading-none hover:bg-(--ld-active)"
            onClick={() => setOpen(false)}
            title={t("settings.common.close")}
          >
            <PanelCollapseIcon />
          </button>
        </div>
      </header>

      <div className="flex flex-col gap-1 border-(--ld-border) border-b px-3 py-2">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("typeExplorer.search")}
          className="w-full rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-1 text-sm focus:border-(--ld-accent) focus:outline-none"
        />
        {activeViewKind && (
          <label className="flex items-center gap-1 text-xs text-(--ld-muted)">
            <input
              type="checkbox"
              checked={filterToActiveDiagram}
              onChange={(e) => setFilterToActiveDiagram(e.target.checked)}
            />
            <span>{t("conceptExplorer.onlyAllowedTypes")}</span>
          </label>
        )}
        {/*
          Layer / owner / tag filters. Collapsed by default so the panel
          stays compact for the common case (just search + the
          active-diagram toggle). When any filter is active we surface a
          "wis filters" affordance + count.
        */}
        <FilterRow
          expanded={filtersExpanded}
          onToggle={() => setFiltersExpanded((v) => !v)}
          activeCount={layerFilter.size + ownerFilter.size + tagFilter.size}
          onClear={() => {
            setLayerFilter(new Set());
            setOwnerFilter(new Set());
            setTagFilter(new Set());
          }}
        />
        {filtersExpanded && (
          <div className="flex flex-col gap-1.5 pt-1">
            <FilterGroup
              label={t("typeExplorer.col.layer")}
              options={filterUniverse.layers}
              selected={layerFilter}
              onChange={setLayerFilter}
            />
            <FilterGroup
              label={t("attributePanel.sectionOwner")}
              options={filterUniverse.owners}
              selected={ownerFilter}
              onChange={setOwnerFilter}
            />
            <FilterGroup
              label={t("conceptExplorer.tags")}
              options={filterUniverse.tags}
              selected={tagFilter}
              onChange={setTagFilter}
              emptyHint={t("conceptExplorer.noTags")}
            />
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {grouped.length === 0 ? (
          <p className="px-3 py-4 text-(--ld-muted) text-xs">
            {query
              ? t("conceptExplorer.noMatches")
              : filterToActiveDiagram && activeViewKind
                ? t("conceptExplorer.noAllowed")
                : t("conceptExplorer.empty")}
          </p>
        ) : (
          <ul className="flex flex-col gap-2 px-2 py-2">
            {grouped.map((g) => {
              const collapsed = isCollapsed(g.typeId);
              return (
                <li key={g.typeId}>
                  <button
                    type="button"
                    className={`flex w-full items-center justify-between rounded border px-2 py-1 text-left font-semibold text-xs ${panelSwatch(colorForLayer(g.layer)).className}`}
                    // Zelfde kleur als in het Typepalet, zodat een type in
                    // beide panelen hetzelfde oogt (zie panelSwatch).
                    style={panelSwatch(colorForLayer(g.layer)).style}
                    onClick={() =>
                      setCollapsedTypes((prev) => ({ ...prev, [g.typeId]: !collapsed }))
                    }
                  >
                    <span>
                      <span className="mr-1 inline-block w-2.5">{collapsed ? "▸" : "▾"}</span>
                      {g.typeLabel}
                    </span>
                    <span className="text-(--ld-muted)">{countTreeNodes(g.tree)}</span>
                  </button>
                  {!collapsed && (
                    <ul className="mt-1 flex flex-col gap-0.5">
                      {g.tree.map((node) => (
                        <ExplorerTreeRow
                          key={node.concept.id}
                          node={node}
                          depth={0}
                          lang={lang}
                          selectedConceptId={selectedConceptId}
                          onActiveDiagram={onActiveDiagram}
                          onSelect={selectConcept}
                          workspaceId={workspaceId}
                          branchId={branchId}
                          multi={multi}
                          visibleOrder={visibleOrder}
                          usageByConceptId={usageByConceptId}
                          onJumpToDiagram={(diagramId) => setActiveDiagram(diagramId)}
                        />
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <ResizeHandle />
    </aside>
  );
}

// ---- Hierarchical tree for same-type Composition --------------------------

import type { Concept, WorkspaceStore } from "@ae/domain";

interface TreeNode {
  concept: Concept;
  children: TreeNode[];
}

interface GroupedConcepts {
  typeId: string;
  typeLabel: string;
  layer: string;
  tree: TreeNode[];
}

/**
 * Build a forest of trees for a single type group, where the children
 * under each concept are ALL its Composition-children (regardless of
 * type). A cross-type child (e.g. an ApplicationFunction part of an
 * ApplicationComponent) shows indented under the parent in the
 * parent's type group AND keeps its own top-level entry in its own
 * type group.
 *
 * Composition convention in this app: source = part, target = whole.
 * Cycles are prevented by tracking visited concepts.
 */
function buildCompositionTree(
  concepts: Concept[],
  store: WorkspaceStore,
  lang: string,
): TreeNode[] {
  const conceptIds = new Set(concepts.map((c) => c.id));

  // Global child-list: parentId → list of child concepts of any type.
  const childrenOf = new Map<string, Concept[]>();
  // Track same-group parents so we know which concepts SHOULD render
  // as top-level in this group vs. which are nested under a sibling.
  const sameGroupParent = new Map<string, string>();
  for (const r of store.listRelations()) {
    if (r.tombstoneInBranch) continue;
    if (r.typeId !== "archimate.Composition") continue;
    const child = store.getConcept(r.sourceConceptId);
    const parent = store.getConcept(r.targetConceptId);
    if (!child || !parent) continue;
    if (child.tombstoneInBranch || parent.tombstoneInBranch) continue;
    const arr = childrenOf.get(parent.id) ?? [];
    arr.push(child);
    childrenOf.set(parent.id, arr);
    if (conceptIds.has(child.id) && conceptIds.has(parent.id) && !sameGroupParent.has(child.id)) {
      sameGroupParent.set(child.id, parent.id);
    }
  }

  const sortByName = (arr: Concept[]) =>
    arr
      .slice()
      .sort((a, b) => resolveBilingual(a.name, lang).localeCompare(resolveBilingual(b.name, lang)));

  const visited = new Set<string>();
  const buildNode = (concept: Concept): TreeNode => {
    visited.add(concept.id);
    const kids = (childrenOf.get(concept.id) ?? []).filter((c) => !visited.has(c.id));
    return { concept, children: sortByName(kids).map(buildNode) };
  };

  // Top-level: concepts in this group that have no SAME-GROUP parent.
  // (Concepts with only cross-group parents still show top-level in
  // their own type group; the parent's group will surface them as
  // indented guests.)
  const topLevel = concepts.filter((c) => !sameGroupParent.has(c.id));
  return sortByName(topLevel).map(buildNode);
}

function countTreeNodes(tree: TreeNode[]): number {
  let n = 0;
  const walk = (node: TreeNode) => {
    n += 1;
    for (const ch of node.children) walk(ch);
  };
  for (const t of tree) walk(t);
  return n;
}

function ExplorerTreeRow({
  node,
  depth,
  lang,
  selectedConceptId,
  onActiveDiagram,
  onSelect,
  workspaceId,
  branchId,
  multi,
  visibleOrder,
  usageByConceptId,
  onJumpToDiagram,
}: {
  node: TreeNode;
  depth: number;
  lang: string;
  selectedConceptId: string | null;
  onActiveDiagram: Set<string>;
  onSelect: (id: string) => void;
  workspaceId: string | null;
  branchId: string | null;
  multi: ReturnType<typeof useMultiSelect>;
  visibleOrder: string[];
  usageByConceptId: Map<string, string[]>;
  onJumpToDiagram: (diagramId: string) => void;
}) {
  const { t } = useTranslation();
  const c = node.concept;
  const name = resolveBilingual(c.name, lang) || t("roadmap.unnamed");
  const inMulti = multi.isSelected(c.id);
  // The row gets "primary" highlight when it's the canvas-selected concept,
  // and a softer "in multi-set" highlight when it's part of a multi-pick.
  const isPrimary = selectedConceptId === c.id;
  const onDiagram = onActiveDiagram.has(c.id);
  // Alleen kijken: selecteren mag, slepen en wissen niet.
  const readOnly = useSession((s) => s.readOnly);
  return (
    <>
      <li>
        <button
          type="button"
          draggable={!readOnly}
          onDragStart={(e) => {
            // If the dragged row is part of an active multi-selection, send
            // ALL selected concept ids — Canvas.onDrop reads this MIME and
            // places each at a cascading offset.
            if (inMulti && multi.size > 1) {
              e.dataTransfer.setData(
                EXISTING_CONCEPT_BULK_MIME,
                JSON.stringify(Array.from(multi.selectedIds)),
              );
            } else {
              e.dataTransfer.setData(EXISTING_CONCEPT_MIME, c.id);
            }
            e.dataTransfer.effectAllowed = "copy";
          }}
          onClick={(e) => {
            const mode = multi.handleClick(c.id, e, visibleOrder);
            // Plain click → single selection: also drive the canvas /
            // attribute panel's "current concept" so the existing UX is
            // preserved. Cmd/Ctrl/Shift clicks stay in multi-mode and do
            // NOT move the canvas selection — the user is building a set.
            if (mode === "single") onSelect(c.id);
          }}
          onKeyDown={(e) => {
            if (e.key === "Delete" || e.key === "Backspace") {
              if (!workspaceId || !branchId) return;
              if (useSession.getState().readOnly) return;
              e.preventDefault();
              const ids = inMulti && multi.size > 1 ? Array.from(multi.selectedIds) : [c.id];
              const ws = useWorkspaceData.getState();
              const langKey: "nl" | "en" = lang === "en" ? "en" : "nl";
              void (async () => {
                const ok = await confirmHardDelete({
                  workspaceId,
                  branchId,
                  conceptIds: ids,
                  lang: langKey,
                  standaloneEdgeCount: 0,
                  conceptName: (id) => ws.store.getConcept(id)?.name,
                });
                if (!ok) return;
                for (const id of ids) {
                  void ws.removeConcept(workspaceId, branchId, id);
                }
                multi.clear();
              })();
            }
          }}
          className={`flex w-full cursor-grab select-none items-center gap-2 rounded border px-2 py-1 text-left text-xs ${
            isPrimary
              ? "border-(--ld-accent) bg-(--ld-active)"
              : inMulti
                ? "border-(--ld-accent) bg-(--ld-active)/60"
                : "border-transparent hover:border-(--ld-border) hover:bg-(--ld-raised)"
          }`}
          style={{ paddingLeft: `${0.5 + depth * 1}rem` }}
          title={
            onDiagram ? t("conceptExplorer.rowTitleOnDiagram") : t("conceptExplorer.rowTitleDrag")
          }
        >
          <span
            className={`block h-1.5 w-1.5 rounded-full ${
              onDiagram ? "bg-(--ld-positive)" : "bg-(--ld-active)"
            }`}
          />
          <span className="truncate">{name}</span>
          <UsageBadge
            usage={usageByConceptId.get(c.id) ?? []}
            lang={lang}
            onJumpToDiagram={onJumpToDiagram}
          />
        </button>
      </li>
      {node.children.map((child) => (
        <ExplorerTreeRow
          key={child.concept.id}
          node={child}
          depth={depth + 1}
          lang={lang}
          selectedConceptId={selectedConceptId}
          onActiveDiagram={onActiveDiagram}
          onSelect={onSelect}
          workspaceId={workspaceId}
          branchId={branchId}
          multi={multi}
          visibleOrder={visibleOrder}
          usageByConceptId={usageByConceptId}
          onJumpToDiagram={onJumpToDiagram}
        />
      ))}
    </>
  );
}

// ---- Filter UI helpers ----------------------------------------------------

/** Top row of the filter section: expand-toggle + count + clear-all. */
function FilterRow({
  expanded,
  onToggle,
  activeCount,
  onClear,
}: {
  expanded: boolean;
  onToggle: () => void;
  activeCount: number;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between text-xs text-(--ld-muted)">
      <button
        type="button"
        className="flex items-center gap-1 hover:text-(--ld-text)"
        onClick={onToggle}
      >
        <span className="inline-block w-2.5">{expanded ? "▾" : "▸"}</span>
        <span>{t("typeExplorer.filters")}</span>
        {activeCount > 0 && (
          <span className="rounded-full bg-(--ld-active) px-1.5 py-px font-semibold text-xs text-(--ld-accent)">
            {activeCount}
          </span>
        )}
      </button>
      {activeCount > 0 && (
        <button
          type="button"
          className="text-(--ld-accent) text-xs hover:text-(--ld-accent)"
          onClick={onClear}
          title={t("conceptExplorer.clearAllFilters")}
        >
          {t("conceptExplorer.clear")}
        </button>
      )}
    </div>
  );
}

/** Multi-select chip group. Click toggles membership. */
function FilterGroup({
  label,
  options,
  selected,
  onChange,
  emptyHint,
}: {
  label: string;
  options: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  emptyHint?: string;
}) {
  const { t } = useTranslation();
  if (options.length === 0) {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="text-xs text-(--ld-muted)">{label}</span>
        <span className="text-xs text-(--ld-muted) italic">
          {emptyHint ?? t("conceptExplorer.noOptions")}
        </span>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-(--ld-muted)">{label}</span>
      <div className="flex flex-wrap gap-1">
        {options.map((opt) => {
          const active = selected.has(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => {
                const next = new Set(selected);
                if (next.has(opt)) next.delete(opt);
                else next.add(opt);
                onChange(next);
              }}
              className={`rounded-full border px-2 py-0.5 text-xs transition ${
                active
                  ? "border-(--ld-accent) bg-(--ld-active) text-(--ld-accent)"
                  : "border-(--ld-control) bg-(--ld-surface) text-(--ld-muted) hover:bg-(--ld-raised)"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Small badge showing how many diagrams reference a concept. Click
 * jumps to the first diagram (or, when there are several, opens a small
 * popover so the user can pick). For minimal UI we just jump to the
 * first one and surface the rest via the title-tooltip.
 */
function UsageBadge({
  usage,
  lang,
  onJumpToDiagram,
}: {
  usage: string[];
  lang: string;
  onJumpToDiagram: (diagramId: string) => void;
}) {
  const { t } = useTranslation();
  if (usage.length === 0) return null;
  const store = useWorkspaceData.getState().store;
  const names = usage
    .map((id) => {
      const d = store.getDiagram(id);
      return d ? resolveBilingual(d.name, lang) || id : id;
    })
    .join(", ");
  return (
    <span
      role="button"
      tabIndex={0}
      title={t("conceptExplorer.usedInTitle", { names })}
      onClick={(e) => {
        e.stopPropagation();
        const first = usage[0];
        if (first) onJumpToDiagram(first);
      }}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        e.stopPropagation();
        const first = usage[0];
        if (first) onJumpToDiagram(first);
      }}
      className="ml-auto rounded-full border border-(--ld-control) bg-(--ld-raised) px-1.5 py-px font-mono text-xs text-(--ld-muted) hover:bg-(--ld-raised)"
    >
      {usage.length}×
    </span>
  );
}
